import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Container, Alert, Spinner, Card, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { sampleService, Sample, SampleListParams } from '../services/sample.service';
import { useSampleStorageSetupGate } from '../hooks/useSampleStorageSetupGate';
import {
  SAMPLE_STORAGE_SETUP_HREF,
  clearStorageSetupModalSession,
} from '../utils/sampleStorageSetupGate';
import {
  isSampleLocalMode,
  isSampleLocalPreferred,
} from '../services/systems/sample-local/sampleLocalMode';
import { useAuth, isAdminUser } from '../hooks/useAuth';
import { useAdminStorageViewAll } from '../hooks/useAdminStorageViewAll';
import { useMdUp } from '../hooks/useLaboSukeMdUp';
import AdminStorageViewToggle from '../components/samples/AdminStorageViewToggle';
import SampleFilterPanel from '../components/samples/SampleFilterPanel';
import BulkOperations from '../components/samples/BulkOperations';
import { useBulkSelection } from '../components/samples/useBulkSelection';
import SampleListPaginationBar from '../components/samples/SampleListPaginationBar';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';
import SampleManagerNavigation from '../components/samples/SampleManagerNavigation';
import styles from './SampleManager.module.css';
import '../components/samples/sampleManagerList.css';
import '../components/samples/sampleManagerList.mobile.css';
import { SampleManagerPageHeading } from '../components/samples/SampleManagerPageHeading';
import SampleManagerCreateActions from '../components/samples/SampleManagerCreateActions';
import SampleManagerModals from '../components/samples/SampleManagerModals';
import SampleListChromeTools, {
  ToolsMenuColVis,
  ToolsMenuExport,
  ToolsMenuHelp,
  ToolsMenuItem,
} from '../components/samples/SampleListChromeTools';
import SampleListStatsCards, {
  type SampleListStatId,
} from '../components/samples/SampleListStatsCards';
import { canManageSample } from '../utils/samplePermissions';
import { useSampleRowMoreExclusive } from '../hooks/useSampleRowMoreExclusive';
import {
  applyStatIdToFilters,
  applyStatQueryToFilters,
  buildSampleListApiParams,
  DEFAULT_SAMPLE_LIST_STATUS_FILTERS,
  hasNonDefaultSampleListFilters,
  parseSampleListStatQuery,
  statusToStatId,
} from '../utils/sampleListFilterParams';
import {
  caseLocationCellToSampleListFilters,
  hasLegacySampleListLocationSearchParams,
  parseLegacySampleListLocationFromSearchParams,
  stripLegacySampleListLocationSearchParams,
} from '../utils/caseLocationUtils';
import {
  resolveSampleListLocationCell,
  stripSampleRouterStateKeys,
  readSampleRouterState,
} from '../utils/samplePageNavigation';
import { logger } from '../utils/logger';
import { loadEditableSample } from '../hooks/useSampleEditLoader';
import { useSampleManagerMutations } from '../hooks/useSampleManagerMutations';
import { useSampleManagerDataTable } from '../hooks/useSampleManagerDataTable';
import { translateAxiosErrorMessage } from '../utils/error.utils';
import { isSampleManagerTourHidden } from '../utils/sampleManagerTour';
const SAMPLE_BULK_MAX = 500;

export default function SampleManager() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [showBulkUsageModal, setShowBulkUsageModal] = useState(false);
  const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
  const [selectedSampleIds, setSelectedSampleIds] = useState<Set<number>>(new Set());
  const selectedSampleIdsRef = useRef<Set<number>>(selectedSampleIds);
  selectedSampleIdsRef.current = selectedSampleIds;
  /** 既定は利用可能のみ。残なし・廃棄は統計ストリップで件数を示し、クリックで表示 */
  const [filters, setFilters] = useState<SampleListParams>(() => ({
    ...DEFAULT_SAMPLE_LIST_STATUS_FILTERS,
  }));
  /** DataTables の PDF action は初回 init のクロージャを保持するため、最新フィルターは ref 経由で読む */
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const [fromCaseLocationLink, setFromCaseLocationLink] = useState(false);
  const pendingListCellConsumedRef = useRef(false);
  const routerListCellStrippedRef = useRef(false);
  const [listPage, setListPage] = useState(1);
  const [listLimit, setListLimit] = useState(50);
  const [showTour, setShowTour] = useState(false);
  /** 同一セッションで自動表示は1回だけ（閉じた直後の effect 再発火で再オープンしない） */
  const tourAutoShownRef = useRef(false);
  /** 一括廃棄・一括削除の確認モーダル */
  const [bulkDangerKind, setBulkDangerKind] = useState<'dispose' | 'delete' | null>(null);

  const tableRef = useRef<HTMLTableElement>(null);
  const createButtonRef = useRef<HTMLButtonElement>(null);
  const { user } = useAuth();
  const isAdmin = isAdminUser(user);
  const { showAdminViewToggle, adminViewAll, setAdminViewAll } = useAdminStorageViewAll(
    isAdmin && !isSampleLocalMode()
  );
  const storageSetupGate = useSampleStorageSetupGate(adminViewAll);
  /** md+: 統計を filter mid、xs: 下帯。二重マウントしない（a11y・DOM） */
  const isMdUp = useMdUp();
  const adminViewAllRef = useRef(adminViewAll);
  adminViewAllRef.current = adminViewAll;

  /** サンプル ID を URL に載せない（?id= 改ざんによる不正操作防止）。旧リンクは除去のみ。 */
  useEffect(() => {
    const legacyId = searchParams.get('id');
    const legacyAction = searchParams.get('action');
    if (!legacyId && !legacyAction) return;
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    next.delete('action');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  /** 概要カード等: `?stat=total|available|…` でストリップを合わせ、クエリは消す */
  useEffect(() => {
    const stat = parseSampleListStatQuery(searchParams.get('stat'));
    if (!stat) return;
    setFilters((prev) => applyStatQueryToFilters(prev, stat));
    setListPage(1);
    const next = new URLSearchParams(searchParams);
    next.delete('stat');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  /** ケース管理からの位置引き継ぎ（Router state）および旧 ?storageRoomId= 等の除去 */
  useEffect(() => {
    if (!pendingListCellConsumedRef.current) {
      pendingListCellConsumedRef.current = true;
      const pending = resolveSampleListLocationCell(location);
      if (pending) {
        setFilters((prev) => ({
          ...prev,
          ...caseLocationCellToSampleListFilters(pending),
        }));
        setFromCaseLocationLink(true);
      }
    }

    if (!routerListCellStrippedRef.current && readSampleRouterState(location).sampleListLocationCell) {
      routerListCellStrippedRef.current = true;
      stripSampleRouterStateKeys(navigate, location, ['sampleListLocationCell']);
    }

    if (!hasLegacySampleListLocationSearchParams(searchParams)) return;

    const legacyCell = parseLegacySampleListLocationFromSearchParams(searchParams);
    if (legacyCell) {
      setFilters((prev) => ({
        ...prev,
        ...caseLocationCellToSampleListFilters(legacyCell),
      }));
      setFromCaseLocationLink(true);
    }
    setSearchParams(stripLegacySampleListLocationSearchParams(searchParams), { replace: true });
  }, [searchParams, setSearchParams, location, navigate]);

  /** ケース管理からの位置リンク（共有保管庫内の他ユーザーサンプルも一覧に含める） */
  const includeSharedStorageSamples =
    fromCaseLocationLink && !adminViewAll ? true : undefined;
  const includeSharedStorageSamplesRef = useRef(includeSharedStorageSamples);
  includeSharedStorageSamplesRef.current = includeSharedStorageSamples;

  // データ取得用パラメータ（キーワード含む・日付は中心日＋前後○から開始・終了日に変換）
  const filtersForApi = useMemo(() => buildSampleListApiParams(filters), [filters]);

  const filtersKey = useMemo(() => JSON.stringify(filtersForApi), [filtersForApi]);

  useEffect(() => {
    setListPage(1);
  }, [filtersKey]);

  useSampleRowMoreExclusive();

  const queryKeyStable = useMemo(
    () =>
      JSON.stringify({
        filtersForApi,
        listPage,
        listLimit,
        adminViewAll,
        includeSharedStorageSamples,
      }),
    [filtersForApi, listPage, listLimit, adminViewAll, includeSharedStorageSamples]
  );

  const { data, isLoading, isFetching, isError, error: listError } = useQuery({
    queryKey: ['samples', queryKeyStable],
    queryFn: () =>
      sampleService.list({
        ...filtersForApi,
        page: listPage,
        limit: listLimit,
        adminViewAll: adminViewAll || undefined,
        includeSharedStorageSamples,
      }),
    placeholderData: (prev) => prev,
  });

  const displayData = useMemo(() => data?.data || [], [data?.data]);
  const listStats = data?.stats;
  const pagination = data?.pagination;
  const hasActiveFilters = hasNonDefaultSampleListFilters(filters);
  const totalCount = listStats?.total ?? pagination?.total ?? 0;
  const isInitialLoading = isLoading && data === undefined;
  const activeStatId = statusToStatId(filters.status);

  const onStatSelect = useCallback((id: SampleListStatId) => {
    setFilters((prev) => applyStatIdToFilters(prev, id));
    setListPage(1);
  }, []);

  /** md mid / xs 帯で共用（呼び出しごとに新規要素 — 同一ノード二重マウント不可） */
  const renderListStatsCards = () =>
    !isInitialLoading && !isError ? (
      <SampleListStatsCards
        stats={{
          total: totalCount,
          available: listStats?.available ?? 0,
          outOfStock: listStats?.outOfStock ?? 0,
          disposed: listStats?.disposed ?? 0,
        }}
        unfiltered={!hasActiveFilters}
        // Zip は単一作業コピーのため「自分」ではなく総数ラベル
        isAdmin={isAdmin || isSampleLocalMode()}
        showDescriptions={false}
        compact
        activeStat={activeStatId}
        onStatSelect={onStatSelect}
        titleOverrides={{
          total: t('sample.stat_filter_total_title'),
          available: t('sample.stat_filter_available_title'),
          outOfStock: t('sample.stat_filter_out_of_stock_title'),
          disposed: t('sample.stat_filter_disposed_title'),
        }}
      />
    ) : null;

  const renderAdminViewToggle = (
    inputId: string,
    className?: string,
    /** xs 統計帯はスイッチのみ（文言は title / aria）で1行に収める */
    mode: 'dense' | 'compact' = 'dense'
  ) =>
    showAdminViewToggle ? (
      <AdminStorageViewToggle
        className={className}
        checked={adminViewAll}
        onChange={setAdminViewAll}
        inputId={inputId}
        dense={mode === 'dense'}
        compact={mode === 'compact'}
        labelKey="sample.admin_view_all_label"
        shortLabelKey="sample.admin_view_all_label_short"
        hintKey="sample.admin_view_all_hint"
      />
    ) : null;

  // 選択されたサンプルを計算（表示中の一覧から）
  const selectedSamples = useMemo(
    () => displayData.filter((s: Sample) => selectedSampleIds.has(s.id)),
    [displayData, selectedSampleIds]
  );

  // 初回利用: 「次回以降非表示」未設定ならローカル Zip / クラウド DB 共通でツアーを自動表示
  useEffect(() => {
    if (isLoading || isError) return;
    if (tourAutoShownRef.current || isSampleManagerTourHidden()) return;
    tourAutoShownRef.current = true;
    const timer = window.setTimeout(() => setShowTour(true), 800);
    return () => window.clearTimeout(timer);
  }, [isLoading, isError]);

  const {
    createMutation,
    updateMutation,
    deleteMutation,
    bulkDisposeMutation,
    bulkDeleteMutation,
    addUsageMutation,
    bulkAddUsageMutation,
  } = useSampleManagerMutations({
    adminViewAll,
    t,
    setError,
    setShowCreateModal,
    setShowEditModal,
    setShowDeleteModal,
    setShowBulkUsageModal,
    setSelectedSample,
    setSelectedSampleIds,
    setBulkDangerKind,
  });

  const handleCreate = async (data: Partial<Sample>) => {
    await createMutation.mutateAsync(data);
  };

  const handleEdit = useCallback(async (sample: Sample) => {
    const result = await loadEditableSample({
      sampleId: sample.id,
      userId: user?.id,
      isAdmin,
      adminViewAll,
      t,
      knownSample: sample,
      logContext: 'sample manager edit',
    });
    if (result.ok === false) {
      setError(result.error);
      return;
    }
    setSelectedSample(result.sample);
    setShowEditModal(true);
  }, [adminViewAll, isAdmin, t, user?.id]);

  const handleUpdate = async (data: Partial<Sample>) => {
    if (selectedSample) {
      await updateMutation.mutateAsync({ id: selectedSample.id, data });
    }
  };

  const handleDelete = useCallback((sample: Sample) => {
    if (!canManageSample(sample, user?.id, { isAdmin, adminViewAll })) {
      setError(t('sample.case_manager.read_only_other_user'));
      return;
    }
    setSelectedSample(sample);
    setShowDeleteModal(true);
  }, [adminViewAll, isAdmin, t, user?.id]);

  const openCreateModal = useCallback(() => setShowCreateModal(true), []);

  const handleConfirmDelete = async () => {
    if (selectedSample) {
      await deleteMutation.mutateAsync(selectedSample.id);
    }
  };

  const ensureBulkSelection = useCallback(
    (emptyKey: string): boolean => {
      if (selectedSampleIds.size === 0) {
        setError(t(emptyKey));
        return false;
      }
      if (selectedSampleIds.size > SAMPLE_BULK_MAX) {
        setError(t('sample.bulk_ids_max_exceeded'));
        return false;
      }
      setError('');
      return true;
    },
    [selectedSampleIds.size, t]
  );

  const handleBulkDisposeRequest = () => {
    if (!ensureBulkSelection('sample.no_samples_selected')) return;
    setBulkDangerKind('dispose');
  };

  const handleBulkDeleteRequest = () => {
    if (!ensureBulkSelection('sample.no_samples_selected_for_delete')) return;
    setBulkDangerKind('delete');
  };

  const handleBulkUsageRequest = () => {
    if (!ensureBulkSelection('sample.no_samples_selected')) return;
    setShowBulkUsageModal(true);
  };

  const handleBulkDangerConfirm = async () => {
    if (!bulkDangerKind) return;
    const ids = Array.from(selectedSampleIds);
    if (bulkDangerKind === 'dispose') {
      await bulkDisposeMutation.mutateAsync(ids);
    } else {
      await bulkDeleteMutation.mutateAsync(ids);
    }
  };

  const handleFilterChange = (newFilters: SampleListParams) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({ ...DEFAULT_SAMPLE_LIST_STATUS_FILTERS });
  };

  const handleLimitChange = useCallback((limit: number) => {
    setListLimit(limit);
    setListPage(1);
  }, []);

  const hasData = Array.isArray(displayData);

  const { dataTableRef } = useSampleManagerDataTable({
    data,
    displayData,
    hasData,
    tableRef,
    isAdmin,
    adminViewAll,
    userId: user?.id,
    language: i18n.language,
    t,
    filtersRef,
    selectedSampleIdsRef,
    adminViewAllRef,
    includeSharedStorageSamplesRef,
    navigate,
    setError,
    setSelectedSample,
    setShowDetailModal,
    setShowUsageModal,
    onEdit: handleEdit,
    onDelete: handleDelete,
  });

  useBulkSelection({
    selectedSampleIds,
    onSelectionChange: setSelectedSampleIds,
    samples: displayData,
    tableRef,
    dataTableRef,
  });

  const listErrorMessage = translateAxiosErrorMessage(
    listError,
    t,
    t('sample.load_failed')
  );

  useEffect(() => {
    if (!storageSetupGate.shouldRedirect) return;
    clearStorageSetupModalSession();
  }, [storageSetupGate.shouldRedirect]);

  if (!storageSetupGate.isReady) {
    return (
      <Container fluid className="mt-2 px-3" id="sample-manager-page">
        <SampleManagerNavigation />
        <SampleManagerPageHeading
          shortLabel={t('navigation.sample_management_short', { defaultValue: 'List' })}
          fullLabel={t('sample.list_title')}
          srOnly
        />
        <div className="text-center py-5" role="status" aria-live="polite" aria-busy="true">
          <Spinner animation="border">
            <span className="visually-hidden">{t('common.loading')}</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (storageSetupGate.shouldRedirect) {
    return <Navigate to={SAMPLE_STORAGE_SETUP_HREF} replace />;
  }

  const createBlockedByMissingStorage = !storageSetupGate.hasStorageRooms;

  return (
    <Container fluid className="mt-2 px-3" id="sample-manager-page">
      <SampleManagerNavigation />
      {/* ナビ短名「一覧」と可視 h1 が縦を奪うため sr-only。役割説明も a11y のみ */}
      <SampleManagerPageHeading
        shortLabel={t('navigation.sample_management_short', { defaultValue: 'List' })}
        fullLabel={t('sample.list_title')}
        srOnly
      />
      <p className="visually-hidden">{t('sample.list_page_role')}</p>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {isError && !data && (
        <Alert variant="danger" className="mt-2">
          {listErrorMessage}
        </Alert>
      )}

      {/* 全選択(xs)は filter leading。詳細トグルは常時表示（⋯ に埋めない） */}
      <SampleFilterPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        onClear={handleClearFilters}
        isAdmin={isAdmin}
        adminStorageViewAll={adminViewAll}
        barVariant="plain"
        mid={
          !isError && isMdUp ? (
            <div
              className="d-flex align-items-center gap-2 flex-shrink-0 keyword-detailed-filter-mid"
              aria-busy={isInitialLoading || undefined}
            >
              {/* 件数セレクトは下バーのみ。min-w-0 だと nowrap 行で統計が 0 幅に潰れる */}
              {isInitialLoading ? (
                <div className="placeholder-glow d-flex gap-2" aria-hidden="true">
                  <span className="placeholder rounded" style={{ width: '4.5rem', height: '1.9rem' }} />
                  <span className="placeholder rounded" style={{ width: '5rem', height: '1.9rem' }} />
                  <span className="placeholder rounded" style={{ width: '4rem', height: '1.9rem' }} />
                </div>
              ) : (
                <>
                  {renderListStatsCards()}
                  {renderAdminViewToggle('admin-storage-view-all-list')}
                </>
              )}
            </div>
          ) : undefined
        }
        trailing={
          <>
            <SampleManagerCreateActions
              createButtonRef={createButtonRef}
              createDisabled={isInitialLoading || createBlockedByMissingStorage}
              createDisabledTitle={
                createBlockedByMissingStorage
                  ? t('dashboard.sample_requires_storage_room', {
                      defaultValue: 'サンプルは先に保管庫を登録する必要があります',
                    })
                  : undefined
              }
              onCreate={openCreateModal}
              compact
            />
            <SampleListChromeTools
              toggleId="sample-list-tools-toggle"
              toggleClassName="sample-list-tools-toggle"
              compactChrome
              help={
                <CollapsibleHelpSection
                  compact
                  expandedPortalId="sample-manager-help-expanded"
                  titleKey="sample.how_to_use_title"
                  descriptionKey="sample.how_to_use_desc"
                  stepsKey={[
                    'sample.how_to_use_step1',
                    'sample.how_to_use_step2',
                    'sample.how_to_use_step3',
                    'sample.how_to_use_step4',
                    'sample.how_to_use_step5',
                  ]}
                  noteKey="sample.how_to_use_note"
                  storageKey="sample_manager"
                />
              }
              menu={
                <>
                  <ToolsMenuHelp pageSelector="#sample-manager-page" />
                  <ToolsMenuItem
                    iconClassName="fas fa-compass"
                    label={t('sample.start_tour')}
                    onClick={() => setShowTour(true)}
                  />
                  <ToolsMenuColVis pageSelector="#sample-manager-page" />
                  <ToolsMenuExport pageSelector="#sample-manager-page" />
                </>
              }
            />
          </>
        }
      />
      {/* phone: 全選択は検索に混ぜず統計帯へ。検索幅と誤タップを避ける */}
      {!isInitialLoading && !isError && !isMdUp ? (
        <div className="mb-1 sample-list-mobile-stats">
          {displayData.length > 0 ? (
            <label
              className="sample-mobile-select-all-hit flex-shrink-0 mb-0"
              title={t('sample.select_all_title', {
                defaultValue: t('sample.select_all'),
              })}
            >
              <input
                type="checkbox"
                id="mobileSelectAllCheckbox"
                className="form-check-input m-0"
                aria-label={t('sample.select_all')}
                title={t('sample.select_all_title', {
                  defaultValue: t('sample.select_all'),
                })}
              />
            </label>
          ) : null}
          {renderListStatsCards()}
          {renderAdminViewToggle(
            'admin-storage-view-all-list-mobile',
            'sample-list-mobile-admin',
            'compact'
          )}
        </div>
      ) : null}
      <div id="sample-manager-help-expanded" />

      {/* 表の直上に置き、選択直後に一括操作が見える（md+ は sticky） */}
      <BulkOperations
        selectedSampleIds={selectedSampleIds}
        onBulkDispose={handleBulkDisposeRequest}
        onBulkDelete={handleBulkDeleteRequest}
        onBulkUsageRecord={handleBulkUsageRequest}
        onClearSelection={() => setSelectedSampleIds(new Set())}
        isDisposing={bulkDisposeMutation.isPending}
        isDeleting={bulkDeleteMutation.isPending}
        isAddingUsage={bulkAddUsageMutation.isPending}
      />

      <Card className="border-0 shadow-sm mb-2">
        <Card.Body className="p-0 position-relative sample-table-card-body">
          {isFetching && data && (
            <div className="sample-list-refetch-spinner" aria-hidden={!isFetching}>
              <Spinner animation="border" size="sm">
                <span className="visually-hidden">{t('common.loading')}</span>
              </Spinner>
            </div>
          )}
          {isInitialLoading ? (
            <div className="text-center py-5" role="status" aria-live="polite">
              <Spinner animation="border">
                <span className="visually-hidden">{t('common.loading')}</span>
              </Spinner>
            </div>
          ) : (
            <>
              {hasActiveFilters && totalCount === 0 && !isError && (
                <div className="text-center py-4 px-3 sample-list-empty-state" role="status">
                  <p className="text-muted mb-2">{t('sample.filter_no_match')}</p>
                  <Button variant="outline-secondary" size="sm" onClick={handleClearFilters}>
                    {t('sample.clear_filters')}
                  </Button>
                </div>
              )}
              {!hasActiveFilters && totalCount === 0 && !isError && (
                <div className="text-center py-5 px-3 sample-list-empty-state" role="status">
                  {createBlockedByMissingStorage ? (
                    <>
                      <p className="text-muted mb-3">
                        {t('sample.list_empty_needs_storage', {
                          defaultValue:
                            'サンプルを登録する前に、保管庫を1つ登録してください。',
                        })}
                      </p>
                      <Button
                        as={Link as any}
                        to={SAMPLE_STORAGE_SETUP_HREF}
                        variant="primary"
                        size="sm"
                        title={t('sample.list_empty_go_to_storage', {
                          defaultValue: '保管庫を登録する',
                        })}
                        aria-label={t('sample.list_empty_go_to_storage', {
                          defaultValue: '保管庫を登録する',
                        })}
                      >
                        <i className="fas fa-warehouse me-1" aria-hidden="true" />
                        {t('sample.list_empty_go_to_storage', {
                          defaultValue: '保管庫を登録する',
                        })}
                      </Button>
                    </>
                  ) : (
                    <>
                      <p className="text-muted mb-3">{t('sample.list_empty')}</p>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={openCreateModal}
                        title={t('sample.modal_create_title', {
                          defaultValue: t('sample.new_registration'),
                        })}
                        aria-label={t('sample.new_registration_short', { defaultValue: '新規' })}
                      >
                        <i className="fas fa-plus me-1" aria-hidden="true" />
                        {t('sample.new_registration_short', { defaultValue: '新規' })}
                      </Button>
                    </>
                  )}
                </div>
              )}
              {/* 0件でも wrapper を残し、絞り込み解除後の DataTables 再マウントコストと計測欠落を避ける */}
              <div
                id="sample-manager-table-wrapper"
                className={`table-responsive ${styles.tableWrap}${isFetching && data ? ' opacity-75' : ''}${
                  totalCount === 0 && !isError ? ' d-none' : ''
                }`}
                aria-hidden={totalCount === 0 && !isError}
              >
                <table
                  ref={tableRef}
                  className={`table table-hover table-bordered mb-0 ${styles.tableFull}`}
                  style={{ width: '100%' }}
                >
                  <thead></thead>
                  <tbody></tbody>
                </table>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {pagination && !isInitialLoading && !isError && (
        <SampleListPaginationBar
          pagination={pagination}
          listLimit={listLimit}
          onLimitChange={handleLimitChange}
          onPageChange={setListPage}
          idPrefix="sample-list-bottom"
          showWhenSinglePage
          className="mb-2 sample-list-pager-bottom"
        />
      )}

      <SampleManagerModals
        showCreateModal={showCreateModal}
        onCloseCreate={() => {
          setShowCreateModal(false);
          setError('');
        }}
        onCreate={handleCreate}
        createLoading={createMutation.isPending}
        adminViewAll={adminViewAll}
        showEditModal={showEditModal}
        onCloseEdit={() => {
          setShowEditModal(false);
          setSelectedSample(null);
          setError('');
        }}
        onUpdate={handleUpdate}
        editSample={selectedSample}
        updateLoading={updateMutation.isPending}
        showDeleteModal={showDeleteModal}
        onCloseDelete={() => {
          setShowDeleteModal(false);
          setSelectedSample(null);
          setError('');
        }}
        onConfirmDelete={handleConfirmDelete}
        deleteSample={selectedSample}
        deleteLoading={deleteMutation.isPending}
        bulkDangerKind={bulkDangerKind}
        onCloseBulkDanger={() => setBulkDangerKind(null)}
        onBulkDangerConfirm={handleBulkDangerConfirm}
        bulkDangerLoading={bulkDisposeMutation.isPending || bulkDeleteMutation.isPending}
        selectedCount={selectedSampleIds.size}
        showDetailModal={showDetailModal}
        onCloseDetail={() => {
          setShowDetailModal(false);
          setSelectedSample(null);
        }}
        detailSampleId={selectedSample?.id ?? null}
        onDetailEdit={(sample) => {
          setSelectedSample(sample);
          setShowEditModal(true);
          setShowDetailModal(false);
        }}
        onDetailDelete={handleDelete}
        showUsageModal={showUsageModal}
        onCloseUsage={() => {
          setShowUsageModal(false);
          setSelectedSample(null);
        }}
        usageSample={selectedSample}
        onUsageSubmit={async (data) => {
          if (!selectedSample) return;
          await addUsageMutation.mutateAsync({
            id: selectedSample.id,
            data: {
              quantity: data.quantity,
              usageDate: data.usageDate,
              purpose: data.purpose,
              notes: data.notes,
              action: data.action,
            },
          });
        }}
        usageLoading={addUsageMutation.isPending}
        showBulkUsageModal={showBulkUsageModal}
        onCloseBulkUsage={() => setShowBulkUsageModal(false)}
        onBulkUsageSubmit={async (records) => {
          await bulkAddUsageMutation.mutateAsync(records);
        }}
        bulkUsageSamples={selectedSamples}
        bulkUsageLoading={bulkAddUsageMutation.isPending}
        showTour={showTour}
        onHideTour={() => setShowTour(false)}
        onTourComplete={() => setShowTour(false)}
        userId={user?.id}
        isAdmin={isAdmin}
        onPermissionError={setError}
      />
    </Container>
  );
}
